/**
 * Print-perfect, personalized one-page Capability Statement for TRITAL Care®.
 *
 * Strategy:
 *  - The entire preview is a single `<section>` block sized to US Letter.
 *  - At @media print the whole page collapses to just that block.
 *  - Uses real assets from /public/certifications/*.
 *  - Every piece of dynamic data (recipient, NAICS, vehicles, note, integrity hash,
 *    QR data URL, generated-at timestamp) comes in via props so the same component
 *    renders identically in the live builder AND inside the /verify snapshot.
 */

import React from "react";

/** Public constants — re-exported so the builder can offer the same picklists. */
export const NAICS_CATALOG = [
  { code: "561320", label: "Temporary Help Services" },
  { code: "561311", label: "Employment Placement Agencies" },
  { code: "561312", label: "Executive Search Services" },
  { code: "621399", label: "Offices of All Other Miscellaneous Health Practitioners" },
  { code: "621610", label: "Home Health Care Services" },
  { code: "621910", label: "Ambulance Services" },
  { code: "622110", label: "General Medical & Surgical Hospitals (served)" },
  { code: "623110", label: "Nursing Care Facilities (Skilled Nursing)" },
  { code: "623312", label: "Assisted Living Facilities for the Elderly" },
];

export const VEHICLE_CATALOG = [
  { key: "direct-prime", label: "Direct contract — Prime" },
  { key: "tier1-sub", label: "Tier-1 diverse subcontractor" },
  { key: "msp-vms", label: "MSP / VMS panel sub-tier" },
  { key: "gsa", label: "GSA Schedule (in progress)" },
  { key: "vha-dha", label: "VA / DHA federal contracts (in progress)" },
  { key: "hub", label: "State of Texas HUB program" },
];

export const CERTS = [
  { k: "nmsdc",  img: "/certifications/nmsdc.png",  line: "NMSDC · MBE Certified",   issuer: "National Minority Supplier Development Council" },
  { k: "hmsdc",  img: null, text: "HMSDC", line: "HMSDC · Regional MBE",   issuer: "Houston MSDC · NMSDC Gulf Coast affiliate" },
  { k: "mbe",    img: "/certifications/mbe.png",    line: "NCTRCA · MBE Certified", issuer: "North Central Texas Regional Certification Agency" },
  { k: "sbe",    img: "/certifications/sbe.jpeg",   line: "NCTRCA · SBE Certified", issuer: "North Central Texas Regional Certification Agency" },
  { k: "sam",    img: "/certifications/sam.png",    line: "SAM.gov Registered",      issuer: "U.S. Federal System for Award Management · Active" },
];

const CORE_COMPETENCIES = [
  "PRN / Per-Diem",
  "Contract / Travel",
  "Direct Hire",
  "Clinical MSP",
  "Credentialing",
  "Skills Verification",
];

const DIFFERENTIATORS = [
  { t: "Healthcare-only vertical", d: "We do not retrofit general staffing. 100% of our credentialing, QA and compliance work is patient-facing." },
  { t: "Joint Commission alignment", d: "Primary-source verification, ongoing competency monitoring and exception logs aligned to HR.01–HR.02." },
  { t: "Named executive sponsor", d: "A single accountable exec per account — QBRs cover diverse-spend, clinical quality and compliance on one page." },
];

export default function CapabilityDocument({
  recipient = { org: "", contact: "", title: "" },
  vehicles = [],
  naics = [],
  note = "",
  generatedAt = new Date().toISOString(),
  integrity = "",
  qrDataUrl = null,
  verifyUrl = "",
}) {
  const selectedNaics = NAICS_CATALOG.filter((n) => naics.includes(n.code));
  const selectedVehicles = VEHICLE_CATALOG.filter((v) => vehicles.includes(v.key));
  const niceDate = new Date(generatedAt).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });

  return (
    <section
      data-testid="capability-document"
      className="cap-doc bg-white text-slate-900 shadow-card border border-slate-200 rounded-2xl overflow-hidden relative print:rounded-none print:border-0 print:shadow-none"
      style={{
        width: "8.5in",
        minHeight: "11in",
        padding: "0.5in",
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "10.25px",
        lineHeight: 1.38,
      }}
    >
      {/* Top brand band */}
      <header className="flex items-start justify-between pb-4 border-b-2 border-trital-navy">
        <div>
          <img
            src="/tritalcare-logo.png"
            alt="TRITAL Care® — Triage Precision. Trusted Workforce."
            className="h-[42px] w-auto select-none object-contain"
            width={685}
            height={194}
            draggable={false}
          />
          <div className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-trital-red/10 text-trital-red text-[8.5px] font-bold uppercase tracking-[0.14em]">
            Capability Statement
          </div>
        </div>
        <div className="text-right max-w-[3in]">
          {recipient.org ? (
            <>
              <div className="text-[8.5px] uppercase tracking-[0.18em] text-slate-400 font-bold">Prepared for</div>
              <div className="mt-1 font-display font-black text-slate-900 leading-tight" style={{ fontSize: "15px" }}>{recipient.org}</div>
              {recipient.contact && (
                <div className="text-[10px] text-slate-600 mt-0.5">
                  {recipient.contact}{recipient.title ? ` · ${recipient.title}` : ""}
                </div>
              )}
            </>
          ) : (
            <div className="text-[9px] text-slate-400 italic">General distribution copy</div>
          )}
          <div className="mt-2 text-[8.5px] text-slate-500">Issued {niceDate}</div>
        </div>
      </header>

      {/* Body — 2 columns */}
      <div className="grid grid-cols-[1.18fr_1fr] gap-5 mt-4">
        {/* LEFT */}
        <div className="space-y-4">
          <Block title="Company snapshot">
            <p className="text-slate-700">
              <span className="font-bold text-trital-navy">TRITAL Care</span> is a healthcare-only workforce partner headquartered in Texas,
              delivering PRN, contract, travel and direct-hire clinicians to hospitals, health systems, SNFs and federally-qualified health centers.
              Clinical quality at parity with the largest prime vendor on the panel is our only mandate.
            </p>
          </Block>

          <Block title="Core competencies">
            <div className="flex flex-wrap gap-1.5">
              {CORE_COMPETENCIES.map((c) => (
                <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[9.5px] font-semibold text-slate-800">
                  {c}
                </span>
              ))}
            </div>
          </Block>

          <Block title="Differentiators">
            <ul className="space-y-1.5">
              {DIFFERENTIATORS.map(({ t, d }) => (
                <li key={t} className="flex gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-trital-red shrink-0" />
                  <div>
                    <span className="font-bold text-slate-900">{t}.</span>{" "}
                    <span className="text-slate-700">{d}</span>
                  </div>
                </li>
              ))}
            </ul>
          </Block>

          {selectedVehicles.length > 0 && (
            <Block title="Contract vehicles">
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                {selectedVehicles.map((v) => (
                  <li key={v.key} className="flex items-center gap-1.5 text-slate-700">
                    <span className="w-1 h-1 rounded-full bg-trital-navy shrink-0" />
                    <span>{v.label}</span>
                  </li>
                ))}
              </ul>
            </Block>
          )}

          {note && (
            <Block title="Note to procurement">
              <p className="text-slate-700 italic border-l-2 border-trital-red pl-2.5">{note}</p>
            </Block>
          )}
        </div>

        {/* RIGHT */}
        <div className="space-y-4">
          <Block title="Certifications & registrations" accent>
            <ul className="space-y-1.5">
              {CERTS.map((c) => (
                <li key={c.k} className="flex items-start gap-2.5">
                  <div className="w-9 h-9 shrink-0 rounded-md bg-white border border-slate-200 p-0.5 flex items-center justify-center">
                    {c.img ? (
                      <img src={c.img} alt="" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <div className="w-full h-full rounded-sm bg-trital-navy text-white font-display font-black text-[8px] flex items-center justify-center leading-none text-center px-0.5">
                        {c.text}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 leading-tight" style={{ fontSize: "10.5px" }}>{c.line}</div>
                    <div className="text-[8.8px] text-slate-500 leading-snug">{c.issuer}</div>
                  </div>
                  <span className="text-[8px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">Active</span>
                </li>
              ))}
            </ul>
          </Block>

          {selectedNaics.length > 0 && (
            <Block title="NAICS codes">
              <ul className="space-y-0.5">
                {selectedNaics.map((n) => (
                  <li key={n.code} className="flex gap-2 text-slate-700">
                    <span className="font-mono font-bold text-trital-navy shrink-0 w-[40px]">{n.code}</span>
                    <span>{n.label}</span>
                  </li>
                ))}
              </ul>
            </Block>
          )}

          <Block title="Readiness">
            <ul className="space-y-1 text-slate-700">
              <li className="flex gap-1.5"><span className="text-emerald-600">✓</span>W-9 and signed certification letters on demand</li>
              <li className="flex gap-1.5"><span className="text-emerald-600">✓</span>COI — GL $2M / PL $3M / WC statutory</li>
              <li className="flex gap-1.5"><span className="text-emerald-600">✓</span>SOC-2 attested credentialing platform</li>
              <li className="flex gap-1.5"><span className="text-emerald-600">✓</span>Joint Commission-aligned onboarding &amp; audit logs</li>
            </ul>
          </Block>
        </div>
      </div>

      {/* Footer band */}
      <footer className="mt-5 pt-4 border-t border-slate-200 grid grid-cols-[1fr_auto] gap-5 items-end">
        <div className="space-y-1 text-slate-700" style={{ fontSize: "9.5px" }}>
          <div className="font-bold text-trital-navy" style={{ fontSize: "10.5px" }}>Contact procurement</div>
          <div><span className="font-semibold">Phone</span> +1 (832) 303-6622 &nbsp;·&nbsp; <span className="font-semibold">Email</span> partners@tritalcare.com</div>
          <div><span className="font-semibold">HQ</span> 5423 Pecan Fall Pl, Missouri City, TX 77459</div>
          <div><span className="font-semibold">Corporate</span> 11104 West Airport Blvd, Ste 213, Stafford, TX 77477</div>
          <div><span className="font-semibold">Web</span> tritalcare.com</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="Scan to verify this document" className="w-[76px] h-[76px] rounded-md border border-slate-200 bg-white" />
          ) : (
            <div className="w-[76px] h-[76px] rounded-md border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[8px] text-slate-400 text-center px-1">QR<br/>unavailable</div>
          )}
          <div className="text-[7.5px] font-bold uppercase tracking-[0.14em] text-trital-navy">Scan to verify</div>
        </div>
      </footer>

      {/* Bottom integrity strip */}
      <div className="mt-3 pt-2 border-t border-dashed border-slate-200 flex items-center justify-between text-[7.5px] text-slate-500">
        <div>
          TRITAL Care<sup>®</sup> is a registered service mark. Generated {niceDate}
          {integrity && <> · Integrity <span className="font-mono font-semibold text-slate-700">{integrity}</span></>}
        </div>
        <div className="truncate max-w-[3.2in] text-right">
          {verifyUrl && <>Verify live: <span className="font-mono">{verifyUrl.replace(/^https?:\/\//, "")}</span></>}
        </div>
      </div>
    </section>
  );
}

function Block({ title, accent = false, children }) {
  return (
    <div>
      <div
        className={`text-[8.5px] font-bold uppercase tracking-[0.16em] pb-1 mb-1.5 border-b ${accent ? "text-trital-red border-trital-red/40" : "text-trital-navy border-slate-200"}`}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}
